import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const sourceSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  description: z.string().max(500).optional().default(""),
});

export default function WebhookSourceSheet({
  open,
  onOpenChange,
  onSubmit,
  editingSource,
}) {
  const isEdit = !!editingSource;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(sourceSchema),
    defaultValues: { name: "", description: "" },
  });

  useEffect(() => {
    if (editingSource) {
      reset({
        name: editingSource.name || "",
        description: editingSource.description || "",
      });
    } else {
      reset({ name: "", description: "" });
    }
  }, [editingSource, reset]);

  const onFormSubmit = async (data) => {
    const success = await onSubmit(data);
    if (success) onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-surface-2 border-border w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Webhook Source" : "Add Webhook Source"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update your webhook source details." : "Create a new webhook source for integrations."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Chartink Alerts"
              className="rounded-sm bg-surface-1 border-border"
            />
            {errors.name && <p className="text-xs text-loss">{errors.name.message}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Describe this webhook source..."
              className="rounded-sm bg-surface-1 border-border"
              rows={3}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm border-border"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-sm" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Source"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
